import { supabase } from "../../lib/supabase";
import { createFoodLogSnapshot } from "./v2FoodLogUtils";
import { prepareCustomFoodPayload, toFoodNumber } from "./v2FoodUtils";
import { V2_TABLES } from "./v2Schema";
import { loadV2CustomFoods, loadV2FoodLogs, saveV2CustomFoods, saveV2FoodLogs } from "./v2Storage";

export { V2_TABLES };

export const V2_DATABASE_WRITE_DISABLED_MESSAGE =
  "This V2 Supabase write helper is prepared but intentionally disabled until its feature prompt wires a safe database integration.";

export const v2Database = Object.freeze({
  profiles: () => supabase.from(V2_TABLES.profiles),
  dailyTargets: () => supabase.from(V2_TABLES.dailyTargets),
  mainFoods: () => supabase.from(V2_TABLES.mainFoods),
  customFoods: () => supabase.from(V2_TABLES.customFoods),
  foodLogs: () => supabase.from(V2_TABLES.foodLogs),
  medicationReminders: () => supabase.from(V2_TABLES.medicationReminders),
  reminderEvents: () => supabase.from(V2_TABLES.reminderEvents),
  approvedTips: () => supabase.from(V2_TABLES.approvedTips),
  quotes: () => supabase.from(V2_TABLES.quotes),
  knowledgeFiles: () => supabase.from(V2_TABLES.knowledgeFiles),
  knowledgeChunks: () => supabase.from(V2_TABLES.knowledgeChunks),
  adminAuditLogs: () => supabase.from(V2_TABLES.adminAuditLogs),
  appSettings: () => supabase.from(V2_TABLES.appSettings),
});

export function getV2UserScopedFilter(query, userId) {
  if (!userId) return query;
  return query.eq("user_id", userId);
}

function requireUserId(userId, helperName) {
  if (!userId) {
    throw new Error(`${helperName} requires a user id.`);
  }
}

function disabledWrite(helperName) {
  throw new Error(`${helperName}: ${V2_DATABASE_WRITE_DISABLED_MESSAGE}`);
}

function throwSupabaseError(error, helperName) {
  if (error) {
    throw new Error(`${helperName} failed: ${error.message}`);
  }
}

export async function getProfile(userId) {
  requireUserId(userId, "getProfile");

  const { data, error } = await v2Database
    .profiles()
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  throwSupabaseError(error, "getProfile");
  return data;
}

export async function upsertProfile() {
  disabledWrite("upsertProfile");
}

export async function getDailyTargets(userId) {
  requireUserId(userId, "getDailyTargets");

  const { data, error } = await v2Database
    .dailyTargets()
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  throwSupabaseError(error, "getDailyTargets");
  return data;
}

export async function upsertDailyTargets() {
  disabledWrite("upsertDailyTargets");
}

export async function listMainFoods({ includeInactive = false, limit = 200 } = {}) {
  let query = v2Database.mainFoods().select("*").order("name_en", { ascending: true });

  if (!includeInactive) {
    query = query.eq("active", true).eq("verified", true);
  }

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;
  throwSupabaseError(error, "listMainFoods");
  return data || [];
}

export async function listCustomFoods(userId, { limit = 200, client = supabase } = {}) {
  requireUserId(userId, "listCustomFoods");

  let query = client
    .from(V2_TABLES.customFoods)
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;
  throwSupabaseError(error, "listCustomFoods");
  return data || [];
}

export async function createCustomFood(userId, input, { client = supabase } = {}) {
  if (!userId) {
    return { ok: false, errors: { user_id: "A registered account is required." }, food: null };
  }

  const { validation, payload } = prepareCustomFoodPayload(input);
  if (!validation.valid) {
    return { ok: false, errors: validation.errors, food: null };
  }

  const row = buildCustomFoodRow(userId, payload);
  const { data, error } = await client
    .from(V2_TABLES.customFoods)
    .insert(row)
    .select("*")
    .single();

  if (error) {
    return { ok: false, errors: { form: "Custom food could not be saved." }, food: null };
  }

  return { ok: true, errors: {}, food: data };
}

export async function deleteCustomFood(userId, foodId, { client = supabase } = {}) {
  if (!userId || !foodId) return { ok: false, deletedId: null };

  const { error } = await client
    .from(V2_TABLES.customFoods)
    .delete()
    .eq("id", foodId)
    .eq("user_id", userId);

  if (error) return { ok: false, deletedId: null };
  return { ok: true, deletedId: foodId };
}

export async function migrateGuestCustomFoodsToAccount(userId, { client = supabase } = {}) {
  if (!userId) return { ok: false, count: 0, error: "A registered account is required." };

  const guestFoods = loadV2CustomFoods({ isGuest: true });
  if (!guestFoods.length) return { ok: true, count: 0, error: "" };

  const rows = [];
  for (const food of guestFoods) {
    const { validation, payload } = prepareCustomFoodPayload(food);
    if (validation.valid) rows.push(buildCustomFoodRow(userId, payload));
  }

  if (!rows.length) {
    saveV2CustomFoods([], { isGuest: true });
    return { ok: true, count: 0, error: "" };
  }

  const { data, error } = await client
    .from(V2_TABLES.customFoods)
    .insert(rows)
    .select("*");

  if (error) {
    return { ok: false, count: 0, error: "Guest custom foods could not be saved to this account." };
  }

  saveV2CustomFoods([], { isGuest: true });
  return { ok: true, count: data?.length || rows.length, error: "" };
}

export async function listFoodLogs(userId, { fromDate, toDate, limit = 500, client = supabase } = {}) {
  requireUserId(userId, "listFoodLogs");

  let query = client
    .from(V2_TABLES.foodLogs)
    .select("*")
    .eq("user_id", userId)
    .order("logged_for_date", { ascending: false })
    .order("created_at", { ascending: false });

  if (fromDate) {
    query = query.gte("logged_for_date", fromDate);
  }

  if (toDate) {
    query = query.lte("logged_for_date", toDate);
  }

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;
  throwSupabaseError(error, "listFoodLogs");
  return data || [];
}

export async function createFoodLog(userId, food, quantity, { date, client = supabase } = {}) {
  if (!userId) {
    return { ok: false, errors: { user_id: "A registered account is required." }, log: null };
  }

  const snapshot = createFoodLogSnapshot(food, quantity, { userId, date });
  if (!snapshot.ok) return snapshot;

  const row = buildFoodLogRow(userId, snapshot.log);
  const { data, error } = await client
    .from(V2_TABLES.foodLogs)
    .insert(row)
    .select("*")
    .single();

  if (error) {
    return { ok: false, errors: { form: "Food could not be logged." }, log: null };
  }

  return { ok: true, errors: {}, log: data };
}

export async function deleteFoodLog(userId, logId, { client = supabase } = {}) {
  if (!userId || !logId) return { ok: false, deletedId: null };

  const { error } = await client
    .from(V2_TABLES.foodLogs)
    .delete()
    .eq("id", logId)
    .eq("user_id", userId);

  if (error) return { ok: false, deletedId: null };
  return { ok: true, deletedId: logId };
}

export async function migrateGuestFoodLogsToAccount(userId, { client = supabase } = {}) {
  if (!userId) return { ok: false, count: 0, error: "A registered account is required." };

  const guestLogs = loadV2FoodLogs({ isGuest: true });
  if (!guestLogs.length) return { ok: true, count: 0, error: "" };

  const rows = guestLogs
    .map((log) => buildFoodLogRow(userId, log))
    .filter((row) => row.food_name_snapshot);

  if (!rows.length) {
    saveV2FoodLogs([], { isGuest: true });
    return { ok: true, count: 0, error: "" };
  }

  const { data, error } = await client
    .from(V2_TABLES.foodLogs)
    .insert(rows)
    .select("*");

  if (error) {
    return { ok: false, count: 0, error: "Guest food logs could not be saved to this account." };
  }

  saveV2FoodLogs([], { isGuest: true });
  return { ok: true, count: data?.length || rows.length, error: "" };
}

export function buildCustomFoodRow(userId, payload = {}) {
  return {
    user_id: userId,
    name: payload.name,
    name_ar: payload.name_ar || null,
    category: payload.category || "Custom",
    quantity: payload.quantity,
    quantity_unit: payload.quantity_unit,
    potassium_mg: payload.potassium_mg,
    phosphorus_mg: payload.phosphorus_mg,
    sodium_mg: payload.sodium_mg,
    protein_g: payload.protein_g,
    icon_key: payload.icon_key || "custom",
  };
}

export function buildFoodLogRow(userId, log = {}) {
  return {
    user_id: userId,
    food_source: log.food_source || "manual",
    food_id: isUuid(log.food_id) ? log.food_id : null,
    food_name_snapshot: String(log.food_name_snapshot || log.food_name || "").trim(),
    quantity: positiveNumber(log.quantity, 1),
    quantity_unit: String(log.quantity_unit || "serving").trim() || "serving",
    potassium_mg_snapshot: zeroOrPositiveNumber(log.potassium_mg_snapshot ?? log.potassium_mg),
    phosphorus_mg_snapshot: zeroOrPositiveNumber(log.phosphorus_mg_snapshot ?? log.phosphorus_mg),
    sodium_mg_snapshot: zeroOrPositiveNumber(log.sodium_mg_snapshot ?? log.sodium_mg),
    protein_g_snapshot: zeroOrPositiveNumber(log.protein_g_snapshot ?? log.protein_g),
    logged_for_date: log.logged_for_date || String(log.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
  };
}

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ""));
}

function positiveNumber(value, fallback) {
  const numeric = toFoodNumber(value);
  return numeric > 0 ? numeric : fallback;
}

function zeroOrPositiveNumber(value) {
  const numeric = toFoodNumber(value);
  return numeric >= 0 ? numeric : 0;
}
